import {SilentRequest} from "@azure/msal-browser"
import {getPca} from "../../authProvider"
import {getConfiguration, getConfigurationItem} from "../../config"
import {getBuFromCache, getlangFromCache} from "../../utils"
import {Roles} from "../../utils"
import {LocalStorageKeys} from "../../utils/local-storage-keys"
import {toastr} from "react-redux-toastr";

export * from "./req_blob"
export * from "./req_family-tech"
export * from "./req_products"
export * from "./req_supplier-parameters"
export * from "./req_family-com"
export * from "./req_multi-modif"
export * from "./req_user"
export * from "./req_vat"
export * from "./req_territoi"
export * from "./req_brands"
export * from "./req_customs"
export * from "./req_product-form"
export * from "./req_translations"
export * from "./req_reference-isg-urls"
export * from "./req_maintenance"
export * from "./req_warranty"

export interface IHttpParams {
    key: string
    value: any
}

/**
 * Error raised by $myFetch for any HTTP status >= 300. Its message has already been displayed in a toastr:
 * callers should not display a second generic message for it.
 */
export class HttpError extends Error {
    status: number
    text: string
    details: string

    constructor(status: number, text: string, details: string) {
        super(text)
        this.name = "HttpError"
        this.status = status
        this.text = text
        this.details = details
    }
}

export const isHttpError = (err: unknown): err is HttpError =>
    !!err && (err as HttpError).name === "HttpError"

const defaultErrorText = (status: number): string => {
    switch (status) {
        case 401:
            return "Votre session a expiré. Veuillez vous reconnecter."
        case 403:
            return "Vous n'avez pas les droits nécessaires pour effectuer cette action."
        case 404:
            return "L'élément demandé est introuvable."
        case 413:
            return "Les fichiers envoyés sont trop volumineux."
        case 502:
        case 503:
        case 504:
            return "Le serveur ne répond pas. Veuillez réessayer plus tard."
        default:
            return `Erreur serveur (HTTP ${status}).`
    }
}

/**
 * Reads an error response whatever its format: { text, details } (API), ProblemDetails, JSON string,
 * plain text or an HTML error page (IIS / App Service). Never returns an empty text.
 */
const readErrorResponse = async (res: Response): Promise<{ text: string; details: string }> => {
    const fallback = defaultErrorText(res.status)
    let raw = ""
    try {
        raw = await res.text()
    } catch {
        return {text: fallback, details: ""}
    }
    try {
        const parsed = JSON.parse(raw)
        if (typeof parsed === "string") {
            return {text: parsed.trim() || fallback, details: ""}
        }
        const text = parsed?.text || parsed?.Text || parsed?.title || parsed?.message
        const details = parsed?.details || parsed?.Details || parsed?.detail || ""
        return {
            text: typeof text === "string" && text.trim() ? text : fallback,
            details: typeof details === "string" ? details : "",
        }
    } catch {
        const isHtml = /^\s*</.test(raw)
        const text = raw.trim()
        return {text: !isHtml && text && text.length < 300 ? text : fallback, details: ""}
    }
}

const encodeParam = (value: any) => encodeURIComponent(value === undefined ? "" : `${value}`)

export const $myFetch = (
    path: string,
    options: any = {headers: new Headers()},
    params: IHttpParams[] = [],
    role: Roles = Roles.SUPPLIER,
    supplierName?: string
) => {
    const request: SilentRequest = getConfiguration().AUTHENTICATION_PARAMETERS
    const pca = getPca()
    return pca
        .acquireTokenSilent(request)
        .then((res) => {
            const myHeaders = options.headers
            myHeaders.set("Authorization", "Bearer " + res.accessToken)

            params = supplierName
                ? params.concat([{key: "supplierName", value: supplierName}])
                : params.concat(getSupplierName(role))
            const lang = getlangFromCache() || "fr"
            const bu = getBuFromCache() || null
            const route = `/api/${path}?lang=${encodeParam(lang)}&marketLeaderBu=${encodeParam(
                bu?.marketLeaderBusinessUnit || null
            )}&supplierBu=${encodeParam(bu?.supplierBusinessUnit || null)}${
                params.length > 0 ? "&" : ""
            }${params.map((param) => encodeParam(param.key) + "=" + encodeParam(param.value)).join("&")}`
            return fetch(route, options)
                .then(async (res) => {
                    if (res.status >= 300) {
                        const {text, details} = await readErrorResponse(res)
                        toastr.error(text, details)
                        throw new HttpError(res.status, text, details)
                    }
                    return res
                })
        })
        .catch((err) => {
            if (
                err.name === "AuthError" ||
                err.name === "InteractionRequiredAuthError" ||
                err.name === "ClientAuthError" ||
                err.name === "ServerError" ||
                err.name === "ClientConfigurationError"
            ) {
                sessionStorage.clear()
                pca.loginRedirect()
            }
            // Errors are propagated: a failed request must never look like a successful one to the caller.
            throw err
        }) as Promise<Response>
}

export const $getAllTopRefs = (): Promise<any> => {
    const myHeaders = new Headers()
    return $myFetch("repairCaratech", {
        headers: myHeaders,
        method: "GET",
    }).then((res) => res.json())
}

export const $repairColor = (): Promise<any> => {
    const myHeaders = new Headers()
    return $myFetch("repairCaratech/color", {
        headers: myHeaders,
        method: "POST",
    }).then((res) => res.json())
}

export const $repairCaratech = (): Promise<any> => {
    const myHeaders = new Headers()
    return $myFetch("repairCaratech", {
        headers: myHeaders,
        method: "POST",
    }).then((res) => res.json())
}

const getSupplierName = (role: Roles) => {
    const stored = localStorage.getItem(LocalStorageKeys.SELECTED_SUPPLIER)
    return role === Roles.MARKET_LEADER
        ? [
            {
                key: "supplierName",
                value: stored,
            },
        ]
        : []
}
